import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles, RotateCcw } from 'lucide-react';
import { fetchPublishDetail, postGenerateComment } from '@/lib/api/publish';
import { isVisibleStatus } from '@/lib/types-publish-gate';
import { ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';

type Props = {
  nodeId: string;
  targetMonth: string;
};

/**
 * v0.3 (S-9): AI月次コメントパネル。
 * R-S9-01対応: pending/generating中のみpolling (5秒間隔)、それ以外は自動更新しない。
 * publishedのみ表示、draftはプレースホルダー (呼び出し元でstatus判定して出し分ける)。
 * v0.5⑤ (2026-09-08 user決定、docs/design/v05-dashboard-consolidation-detail.md §3): Lite版と同じ
 * 「今すぐ生成する」ボタンをFull版にも追加。既存の店舗別詳細画面 (StoreDashboardPage.tsx) と
 * 新統合ダッシュボードの両方がこのコンポーネントを共用するため、ここに1箇所実装すれば両画面に反映される。
 */
export const AiPeriodCommentPanel = ({ nodeId, targetMonth }: Props) => {
  const qc = useQueryClient();
  // dashboard:view があればこのパネル自体は表示されるが、生成ボタンは dashboard:update 保有者限定
  // (バックエンドの PUT /memo と同じ権限、CLAUDE.md §6e: メモ編集と同じ「店長」相当のみ許可)
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const canGenerate = hasPermissionForFeatureAction('dashboard', 'update');

  const { data, refetch } = useQuery({
    queryKey: ['publish-detail', nodeId, targetMonth],
    queryFn: () => fetchPublishDetail(nodeId, targetMonth),
    refetchInterval: (query) => {
      const status = query.state.data?.ai_comment?.status;
      return status === 'pending' || status === 'generating' ? 5000 : false;
    },
  });

  const generateMut = useMutation({
    mutationFn: () => postGenerateComment(nodeId, targetMonth),
    // レスポンス形がPublishDetailResponseと異なるため cache へ直接書かず refetch で正典を引き直す
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['publish-detail', nodeId, targetMonth] }); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'AIコメントの生成に失敗しました'),
  });

  const comment = data?.ai_comment;
  const publishStatus = data?.publish_status?.status;
  const isVisible = isVisibleStatus(publishStatus ?? 'draft');

  if (!isVisible) {
    return (
      <div className="rounded-lg border border-surface-border bg-surface-sunken px-4 py-3 text-[13px] text-text-muted">
        公開後にAIコメントが表示されます
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-surface-border bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-[12px] font-extrabold text-text-sub">
          <Sparkles size={14} className="text-brand-orange" aria-hidden /> AIコメント
          {publishStatus === 'preliminary' && (
            // ⚠️ design-and-code MEDIUM 指摘 (D-186、StatusIcon.tsx と同型のコントラスト不足の再発)
            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text font-bold">速報値</span>
          )}
        </div>
        {/* v0.5⑤: dashboard:update 保有者のみ「今すぐ生成」できる (PUT /memo と同じ権限) */}
        {canGenerate && comment?.status !== 'generating' && comment?.status !== 'pending' && (
          <button type="button" onClick={() => generateMut.mutate()} disabled={generateMut.isPending}
            className="flex items-center gap-1 text-[11px] font-bold text-text-sub hover:text-text-main disabled:opacity-50 min-h-[44px] whitespace-nowrap">
            <RotateCcw size={12} className={generateMut.isPending ? 'animate-spin' : ''} />
            {generateMut.isPending ? '生成中…' : (comment ? '再生成' : '生成する')}
          </button>
        )}
      </div>
      {!comment && !generateMut.isPending && <p className="text-[13px] text-text-muted">生成待機中…</p>}
      {comment && (comment.status === 'pending' || comment.status === 'generating') && (
        <p className="text-[13px] text-text-sub">生成中…</p>
      )}
      {comment?.status === 'succeeded' && comment.regeneration_failed_at && (
        <p className="text-[12px] text-alert-text mb-1.5">
          ⚠ このコメントは速報値に基づいています (確定値での再生成に失敗しました)
        </p>
      )}
      {comment?.status === 'succeeded' && (
        <p className="text-[13px] text-text-body whitespace-pre-wrap">{comment.commentBody}</p>
      )}
      {comment?.status === 'succeeded' && (comment.used_instructions?.length ?? 0) > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          <span className="text-[11px] text-text-sub">使用した指示:</span>
          {comment.used_instructions!.map((ins) => (
            <span key={ins.instruction_id}
              className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border break-words">
              {ins.title}
            </span>
          ))}
          {/* v0.5⑤: 手動再生成が可能になったため「1回だけ」という旧文言は不正確 (canGenerate保有者向けには出さない) */}
          {!canGenerate && (
            <span className="w-full text-[11px] text-text-sub mt-1">
              公開時点の設定で生成されます (店長等が「再生成」すると最新の設定で作り直せます)
            </span>
          )}
        </div>
      )}
      {comment?.status === 'failed' && (
        <div className="space-y-2">
          <p className="text-[13px] text-danger-text">生成に失敗しました: {comment.errorMessage}</p>
          <button type="button" onClick={() => refetch()}
            className="inline-flex items-center gap-1 text-[12px] font-bold text-text-sub underline hover:text-text-main focus-ring min-h-[44px]">
            <RotateCcw size={12} /> 再確認
          </button>
        </div>
      )}
    </div>
  );
};
